import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

// WhatsApp message for every filled order on the admin's OKX account.
// Keys are write-only here: the server never sends them back.
const OkxTradeAlerts = () => {
  const [s, setS] = useState({ enabled: false, send_to: "", has_keys: false });
  const [keys, setKeys] = useState({ api_key: "", secret: "", passphrase: "" });
  const [tgToken, setTgToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);       // [{id, kind, text}] once loaded
  const [picked, setPicked] = useState({});

  const loadOpen = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/okx/open`);
      setOpen(r.data.items || []);
      setPicked({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load open trades");
    }
    setBusy(false);
  };

  const sendOpen = async () => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    if (!ids.length) return toast.error("Tick at least one");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/okx/open/send`, { ids });
      toast.success(`Sent ${r.data.sent}`);
      setPicked({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    }
    setBusy(false);
  };

  useEffect(() => {
    axios.get(`${API}/okx/settings`).then((r) => setS(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/okx/settings`, {
        enabled: s.enabled, send_spot: s.send_spot, send_futures: s.send_futures, send_to: s.send_to, tg_chat: s.tg_chat, tg_token: tgToken, welcome_enabled: s.welcome_enabled, welcome_text: s.welcome_text,
        start_balance: s.start_balance, bot_faq: s.bot_faq, ...keys,
      });
      setS(r.data);
      setKeys({ api_key: "", secret: "", passphrase: "" });
      setTgToken("");
      toast.success(tgToken && !r.data.tg_chat ? "Saved — channel not found yet: add the bot as admin of the channel, then Save again" : "OKX alerts saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
    setBusy(false);
  };

  const summary = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/okx/summary`);
      toast.success("Results sent to the group");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/okx/test`);
      toast.success(`OKX key works (${r.data.permissions || "read"}) — test message: ${r.data.sent || "nowhere set"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
    setBusy(false);
  };

  const field = "w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 text-sm text-white font-mono outline-none focus:border-[#00C2FF]";
  const ph = (name) => (s.has_keys ? `${name} (saved — blank keeps it)` : name);

  return (
    <div className="bg-[#111113] border border-[#27272A] rounded-2xl p-4 sm:p-6 space-y-4 mt-4" data-testid="okx-trade-alerts">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">OKX trade alerts</h2>
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} />
          Enabled
        </label>
      </div>
      <p className="text-xs text-gray-500">
        Every order that fills on your OKX account (spot, margin, perpetual, futures) is posted to your Telegram channel (or WhatsApp) within a minute.
        Use a <b>read-only</b> API key: OKX → Profile → API → Create API key → permission <b>Read</b> only.
      </p>
      <div className="flex items-center gap-5 flex-wrap text-sm text-gray-200">
        <span className="text-xs text-gray-500">Post automatically:</span>
        {[["send_futures", "Futures trades"], ["send_spot", "Spot trades"]].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2">
            <input type="checkbox" checked={s[k] !== false} onChange={(e) => setS({ ...s, [k]: e.target.checked })} />
            {label}
          </label>
        ))}
        <span className="text-[11px] text-gray-500">Unticked ones are not posted. Futures still count in Balance &amp; results either way.</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input className={field} type="password" placeholder={ph("1 · API key")} value={keys.api_key}
               onChange={(e) => setKeys({ ...keys, api_key: e.target.value })} />
        <input className={field} type="password" placeholder={ph("2 · Secret key")} value={keys.secret}
               onChange={(e) => setKeys({ ...keys, secret: e.target.value })} />
        <input className={field} type="password" placeholder={ph("3 · Passphrase")} value={keys.passphrase}
               onChange={(e) => setKeys({ ...keys, passphrase: e.target.value })} />
      </div>
      <div className="border-t border-[#27272A] pt-3 space-y-2">
        <div className="text-sm font-bold text-white">Telegram channel</div>
        <p className="text-[11px] text-gray-500">
          Telegram → @BotFather → /newbot → paste the token it gives you below. Then in your channel: Administrators →
          Add admin → your bot (allow posting). Leave the channel box blank and Save — it's found by itself.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input className={field} type="password" value={tgToken} onChange={(e) => setTgToken(e.target.value)}
                 placeholder={s.has_tg_token ? "Bot token (saved — blank keeps it)" : "Bot token from @BotFather"} />
          <input className={field} placeholder="Channel — blank = find it, or @name / t.me/name"
                 value={s.tg_chat || ""} onChange={(e) => setS({ ...s, tg_chat: e.target.value })} />
        </div>
      </div>
      <div>
        <input className={field} placeholder={s.has_tg_token ? "WhatsApp — blank = don't send on WhatsApp" : "WhatsApp send to — blank = your admin number, or a phone, or a group id (…@g.us)"}
               value={s.send_to} onChange={(e) => setS({ ...s, send_to: e.target.value })} />
      </div>
      <div className="border-t border-[#27272A] pt-3 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm font-bold text-white">Open trades</div>
          <button onClick={loadOpen} disabled={busy || !s.has_keys}
                  className="px-3 py-1.5 rounded-md border border-[#27272A] text-gray-200 text-xs disabled:opacity-40">
            {open ? "Reload" : "Load open trades"}
          </button>
        </div>
        {open && (open.length === 0 ? (
          <p className="text-xs text-gray-500">No open positions or pending orders right now.</p>
        ) : (
          <>
            <p className="text-[11px] text-gray-500">Tick the ones to post. Only those go to the group, with current numbers.</p>
            <div className="space-y-1">
              {open.map((it) => (
                <label key={it.id} className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${picked[it.id] ? "border-[#00E054]/60 bg-[#00E054]/5" : "border-[#27272A] bg-[#0A0A0A]"}`}>
                  <input type="checkbox" className="mt-1" checked={!!picked[it.id]}
                         onChange={(e) => setPicked({ ...picked, [it.id]: e.target.checked })} />
                  <span className="text-xs text-gray-200 whitespace-pre-line">{it.text}</span>
                </label>
              ))}
            </div>
            <button onClick={sendOpen} disabled={busy || !Object.values(picked).some(Boolean)}
                    className="px-3 py-1.5 rounded-md bg-[#00E054] text-black text-xs font-bold disabled:opacity-40">
              Send selected to channel
            </button>
          </>
        ))}
      </div>
      <div className="border-t border-[#27272A] pt-3 space-y-2">
        <div className="text-sm font-bold text-white">Balance &amp; results</div>
        <div className="flex items-center gap-2 flex-wrap">
          <input className={`${field} max-w-[220px]`} type="number" step="any" placeholder="Starting balance (USDT)"
                 value={s.start_balance ?? ""} onChange={(e) => setS({ ...s, start_balance: e.target.value })} />
          <span className="text-[11px] text-gray-500">Changing it and saving starts the count again from now.</span>
        </div>
        {s.stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {[
              ["Balance", `${s.stats.balance.toFixed(2)} USDT`],
              ["Profit", `${s.stats.total >= 0 ? "+" : ""}${s.stats.total.toFixed(4)} (${s.stats.pct.toFixed(2)}%)`],
              ["Closed trades", `${s.stats.trades} · ${s.stats.wins} wins`],
              ["Best / worst", `${s.stats.best.toFixed(3)} / ${s.stats.worst.toFixed(3)}`],
            ].map(([k, v]) => (
              <div key={k} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-2">
                <div className="text-gray-500">{k}</div>
                <div className={`font-mono ${k === "Profit" ? (s.stats.total >= 0 ? "text-[#00E054]" : "text-red-400") : "text-white"}`}>{v}</div>
              </div>
            ))}
          </div>
        )}
        <button onClick={summary} disabled={busy || !s.stats}
                className="px-3 py-1.5 rounded-md border border-[#00E054]/50 text-[#00E054] text-xs disabled:opacity-40">
          Send results to channel
        </button>
      </div>
      <div className="border-t border-[#27272A] pt-3 space-y-2">
        <label className="flex items-center gap-2 text-sm text-gray-200">
          <input type="checkbox" checked={!!s.welcome_enabled}
                 onChange={(e) => setS({ ...s, welcome_enabled: e.target.checked })} />
          Post this in the group when new people join
        </label>
        <p className="text-[11px] text-gray-500">
          Posted in the group itself — at most once an hour, covering everyone who joined since. Never sent privately:
          private messages to people who never wrote to the bot get the number restricted by WhatsApp for spam.
        </p>
        <textarea className={`${field} min-h-[120px] font-sans`} value={s.welcome_text || ""}
                  onChange={(e) => setS({ ...s, welcome_text: e.target.value })} />
        {s.welcome_checked && <div className="text-[11px] text-gray-500">Last group check: {s.welcome_checked}</div>}
        <div className="text-sm font-bold text-white pt-2">What the bot answers about the group</div>
        <p className="text-[11px] text-gray-500">
          When someone messages the bot privately about the trading group, it answers from this (in their language).
        </p>
        <textarea className={`${field} min-h-[160px] font-sans`} value={s.bot_faq || ""}
                  onChange={(e) => setS({ ...s, bot_faq: e.target.value })} />
        {s.welcome_last && <div className="text-[11px] text-gray-500">Last: {s.welcome_last.split(" ")[0]} new member(s) welcomed</div>}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={save} disabled={busy} className="px-4 py-2 rounded-md bg-[#00C2FF] text-black text-sm font-bold disabled:opacity-50">
          Save OKX settings
        </button>
        <button onClick={test} disabled={busy || !s.has_keys}
                className="px-4 py-2 rounded-md border border-[#27272A] text-gray-200 text-sm disabled:opacity-40">
          Test (sends a message)
        </button>
      </div>
      {(s.last_error || s.last_trade || s.last_ok) && (
        <div className={`text-xs rounded-lg p-2 border ${s.last_error ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-[#27272A] text-gray-400"}`}>
          {s.last_error ? `⚠️ ${s.last_error}` : `Last check ${new Date(s.last_ok).toLocaleTimeString()}`}
          {s.last_trade && <div className="text-gray-400 mt-1">Last trade sent: {s.last_trade}</div>}
        </div>
      )}
    </div>
  );
};

export default OkxTradeAlerts;
